import React, { useEffect, useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  TextField, 
  IconButton, 
  Box,
  Snackbar,
  Alert,
  Paper,
  Stack,
  FormControlLabel,
  Switch,
  Slider,
  Collapse,
  CircularProgress,
  Chip,
  Tooltip,
  Checkbox
} from '@mui/material';
import { ICollection, IUser } from '../types/damTypes';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SearchIcon from '@mui/icons-material/Search';
import { QRCodeSVG } from 'qrcode.react';
import axiosInstance from '../services/axiosInstance';
import { API_BASE_URL } from '../config';

interface ShareLinkData {
  shareToken: string;
  allowDownload: boolean;
  passwordEnabled?: boolean;
  expiresAt?: string;
  maxViews?: number;
  viewCount: number;
  settings: {
    customMessage?: string;
    includeMetadata?: boolean;
    banner?: {
      enabled?: boolean;
      imagePath?: string;
      height?: number;
      focalY?: number;
    };
  };
}

interface ShareCollectionDialogProps {
  open: boolean;
  onClose: () => void;
  collection: ICollection | null;
  onSettingsSaved?: () => void;
}

interface BannerCandidate {
  imagePath: string;
  thumbnailPath?: string;
  title: string;
  source: 'asset' | 'upload';
  assetId?: string;
}

const sectionCardSx = {
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: '10px',
  p: 2.5,
  mb: 2,
  bgcolor: '#fafafa',
} as const;

const sectionTitleSx = {
  fontWeight: 600,
  fontSize: '0.82rem',
  color: '#444',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
  mb: 1.5,
};

function getOwnerName(collection: ICollection | null): string | null {
  if (!collection) return null;
  const owner = (collection as any).createdBy || collection.owner;
  if (!owner || typeof owner === 'string') return null;
  const u = owner as IUser;
  if (u.firstName || u.lastName) return [u.firstName, u.lastName].filter(Boolean).join(' ');
  if (u.email) return u.email;
  return null;
}

function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function toDateTimeInputValue(value?: string | Date | null): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
}

function getBannerImageUrl(imagePath?: string | null): string {
  if (!imagePath) return '';
  if (/^https?:\/\//i.test(imagePath)) return imagePath;
  return `${API_BASE_URL}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;
}

const DEFAULT_SHARE_EXPIRY_WARNING = 'OBS! den delade länken slutar fungera om 1 månad.';

const ShareCollectionDialog: React.FC<ShareCollectionDialogProps> = ({ open, onClose, collection, onSettingsSaved }) => {
  const [email, setEmail] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');
  const [showQrCode, setShowQrCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [shareToken, setShareToken] = useState('');
  const [password, setPassword] = useState('');
  const [passwordEnabled, setPasswordEnabled] = useState(false);
  const [passwordToggle, setPasswordToggle] = useState(false);
  const [removePassword, setRemovePassword] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');
  const [allowDownload, setAllowDownload] = useState(true);
  const [maxViews, setMaxViews] = useState<string>('');
  const [customMessage, setCustomMessage] = useState('');
  const [includeMetadata, setIncludeMetadata] = useState(false);
  const [bannerEnabled, setBannerEnabled] = useState(false);
  const [bannerImagePath, setBannerImagePath] = useState('');
  const [bannerHeight, setBannerHeight] = useState(300);
  const [bannerFocalY, setBannerFocalY] = useState(50);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerSearchQuery, setBannerSearchQuery] = useState('');
  const [bannerSearchResults, setBannerSearchResults] = useState<BannerCandidate[]>([]);
  const [searchingBannerAssets, setSearchingBannerAssets] = useState(false);
  const [bannerSearchPerformed, setBannerSearchPerformed] = useState(false);
  const [recentBanners, setRecentBanners] = useState<BannerCandidate[]>([]);
  const [loadingRecentBanners, setLoadingRecentBanners] = useState(false);
  const [viewCount, setViewCount] = useState(0);

  const [activeFrom, setActiveFrom] = useState('');
  const [activeTo, setActiveTo] = useState('');
  const [neverAutoDelete, setNeverAutoDelete] = useState(false);
  const [shareExpiryEdited, setShareExpiryEdited] = useState(false);

  const collectionId = collection?._id;
  const shareUrl = shareToken ? `${window.location.origin}/share/${shareToken}` : '';
  const ownerName = getOwnerName(collection);
  const defaultShareExpiry = collection?.createdAt
    ? toDateTimeInputValue(addMonths(new Date(collection.createdAt), 1))
    : '';
  const showDefaultShareExpiryWarning = Boolean(expiresAt && defaultShareExpiry && expiresAt === defaultShareExpiry && !shareExpiryEdited);

  useEffect(() => {
    const loadShareLink = async () => {
      if (!open || !collectionId) return;
      try {
        setLoading(true);
        const resp = await axiosInstance.get(`/api/share/collections/${collectionId}/share`);
        const link: ShareLinkData = resp.data.shareLink;
        if (link) {
          setShareToken(link.shareToken);
          setAllowDownload(link.allowDownload);
          setViewCount(link.viewCount || 0);
          setPasswordEnabled(!!(link as any).passwordEnabled);
          setPasswordToggle(!!(link as any).passwordEnabled);
          setRemovePassword(false);
          setPassword('');
          if (link.expiresAt) {
            setExpiresAt(toDateTimeInputValue(link.expiresAt));
          } else {
            setExpiresAt('');
          }
          setShareExpiryEdited(false);
          setMaxViews(link.maxViews ? String(link.maxViews) : '');
          setCustomMessage(link.settings?.customMessage || '');
          setIncludeMetadata(link.settings?.includeMetadata || false);
          if (link.settings?.banner) {
            setBannerEnabled(link.settings.banner.enabled || false);
            setBannerImagePath(link.settings.banner.imagePath || '');
            setBannerHeight(link.settings.banner.height || 300);
            setBannerFocalY(link.settings.banner.focalY || 50);
          } else {
            setBannerEnabled(false);
            setBannerImagePath('');
            setBannerHeight(300);
            setBannerFocalY(50);
          }
        }

        if (collection) {
          const af = (collection as any).activeFrom;
          const at = (collection as any).activeTo;
          setActiveFrom(toDateTimeInputValue(af));
          setActiveTo(toDateTimeInputValue(at));
          setNeverAutoDelete(!!(collection as any).neverAutoDelete);
        }
      } catch (e: any) {
        console.error('Could not load share link:', e?.message || e);
        showSnackbar('Kunde inte ladda delningslänk', 'error');
      } finally {
        setLoading(false);
      }
    };
    loadShareLink();
  }, [open, collectionId, collection]);

  useEffect(() => {
    if (open) setShowQrCode(false);
  }, [open]);

  useEffect(() => {
    const loadRecentBanners = async () => {
      if (!open) return;
      try {
        setLoadingRecentBanners(true);
        const resp = await axiosInstance.get('/api/share/banners/recent');
        setRecentBanners(Array.isArray(resp.data?.banners) ? resp.data.banners : []);
      } catch (err: any) {
        console.error('Could not load recent banners:', err?.message || err);
      } finally {
        setLoadingRecentBanners(false);
      }
    };
    loadRecentBanners();
  }, [open]);

  const showSnackbar = (msg: string, severity: 'success' | 'error' | 'info') => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSelectBannerCandidate = (candidate: BannerCandidate) => {
    setBannerImagePath(candidate.imagePath);
    setBannerEnabled(true);
    showSnackbar('Banner vald', 'success');
  };

  const handleBannerSearch = async () => {
    const query = bannerSearchQuery.trim();
    if (query.length < 2) {
      showSnackbar('Skriv minst två tecken för att söka bannerbild', 'info');
      return;
    }

    try {
      setSearchingBannerAssets(true);
      setBannerSearchPerformed(true);
      const resp = await axiosInstance.get('/api/share/banners/search', {
        params: { q: query, limit: 12 },
      });
      setBannerSearchResults(Array.isArray(resp.data?.banners) ? resp.data.banners : []);
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Kunde inte söka bannerbilder', 'error');
    } finally {
      setSearchingBannerAssets(false);
    }
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingBanner(true);
      const formData = new FormData();
      formData.append('banner', file);
      const resp = await axiosInstance.post('/api/share/banners/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBannerImagePath(resp.data.path);
      setBannerEnabled(true);
      showSnackbar('Banner uppladdad', 'success');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Kunde inte ladda upp banner', 'error');
    } finally {
      setUploadingBanner(false);
      try { (e.target as any).value = ''; } catch {}
    }
  };

  const handleSaveSettings = async () => {
    if (!collectionId) return;
    const maxViewsNumber = Number(maxViews);
    const hasMaxViews = maxViews !== '' && Number.isFinite(maxViewsNumber) && maxViewsNumber > 0;
    if (!activeTo && !expiresAt && !hasMaxViews && !neverAutoDelete) {
      showSnackbar('Ange minst ett giltighetsvillkor: samlingens slutdatum, delningslänkens utgångsdatum, max visningar eller "radera aldrig".', 'error');
      return;
    }

    try {
      setSaving(true);

      // Save share link settings
      const body: any = { 
        allowDownload, 
        includeMetadata,
        customMessage: customMessage || null
      };
      if (!passwordToggle) {
        if (removePassword || passwordEnabled) body.password = null;
      } else if (password) {
        body.password = password;
      }
      body.expiresAt = expiresAt ? new Date(expiresAt).toISOString() : null;
      body.maxViews = hasMaxViews ? maxViewsNumber : null;
      body.banner = {
        enabled: bannerEnabled,
        imagePath: bannerImagePath || null,
        height: bannerHeight,
        focalY: bannerFocalY
      };
      await axiosInstance.put(`/api/share/collections/${collectionId}/share`, body);

      // Save collection scheduling
      await axiosInstance.put(`/api/collections/${collectionId}`, {
        activeFrom: activeFrom ? new Date(activeFrom).toISOString() : null,
        activeTo: activeTo ? new Date(activeTo).toISOString() : null,
        neverAutoDelete,
      });
      
      setPassword('');
      setRemovePassword(false);
      if (body.password === null) { setPasswordEnabled(false); setPasswordToggle(false); }
      if (typeof body.password === 'string' && body.password.length >= 4) { setPasswordEnabled(true); setPasswordToggle(true); }
      
      onSettingsSaved?.();
      showSnackbar('Inställningar sparade', 'success');
    } catch (err: any) {
      showSnackbar(err?.response?.data?.message || 'Kunde inte spara inställningar', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  const handleCopyLink = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl)
      .then(() => showSnackbar('Länk kopierad!', 'success'))
      .catch(() => showSnackbar('Kunde inte kopiera länk', 'error'));
  };
  
  const handleEmailShare = () => {
    const title = collection?.name || 'Samling';
    const subject = encodeURIComponent(`Delad samling: ${title}`);
    const body = encodeURIComponent(`Kolla in denna samling: ${title}\n\n${shareUrl}`);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    setEmail('');
  };

  const handlePreviewSharePage = () => {
    if (shareUrl) window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadQRCode = () => {
    const svgElement = document.getElementById('collection-qrcode');
    if (!svgElement || !(svgElement instanceof SVGSVGElement)) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = 200;
    canvas.height = 200;
    const img = new Image();
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const link = document.createElement('a');
      link.download = `${(collection?.name || 'samling').replace(/\s+/g, '_')}_qrcode.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  };

  const renderBannerCandidateGrid = (candidates: BannerCandidate[], emptyMessage: string) => {
    if (candidates.length === 0) {
      return (
        <Typography sx={{ fontSize: '0.75rem', color: '#999', py: 1 }}>
          {emptyMessage}
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 1 }}>
        {candidates.map((candidate) => {
          const thumbnailUrl = getBannerImageUrl(candidate.thumbnailPath || candidate.imagePath);
          const isSelected = candidate.imagePath === bannerImagePath;
          return (
            <Box
              key={`${candidate.source}-${candidate.assetId || candidate.imagePath}`}
              role="button"
              tabIndex={0}
              onClick={() => handleSelectBannerCandidate(candidate)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleSelectBannerCandidate(candidate);
                }
              }}
              sx={{
                cursor: 'pointer',
                border: isSelected ? '2px solid #072f53' : '1px solid #e3e8ef',
                borderRadius: '8px',
                overflow: 'hidden',
                bgcolor: '#fff',
                minWidth: 0,
                boxShadow: isSelected ? '0 0 0 2px rgba(7,47,83,0.12)' : 'none',
                '&:hover': { borderColor: '#072f53' },
              }}
            >
              <Box
                sx={{
                  height: 54,
                  bgcolor: '#f4f6f8',
                  backgroundImage: thumbnailUrl ? `url(${thumbnailUrl})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {!thumbnailUrl && <ImageIcon sx={{ color: '#9aa4b2' }} />}
              </Box>
              <Box sx={{ px: 0.75, py: 0.6 }}>
                <Typography noWrap title={candidate.title} sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#334155' }}>
                  {candidate.title}
                </Typography>
                <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                  {candidate.source === 'asset' ? 'Athena' : 'Uppladdad'}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };
  
  return (
    <>
      <Dialog 
        open={open && !!collection}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ pb: 0.5, pt: 2.5, px: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#222' }}>
                Inställningar
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: '#666', mt: 0.25 }}>
                {collection?.name}
              </Typography>
              {ownerName && (
                <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                  <PersonOutlineIcon sx={{ fontSize: 14, color: '#999' }} />
                  <Typography sx={{ fontSize: '0.75rem', color: '#999' }}>
                    Skapad av {ownerName}
                  </Typography>
                </Box>
              )}
            </Box>
            <IconButton onClick={onClose} sx={{ mt: -0.5, mr: -1, color: '#999' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              {/* === SECTION 1: DELNINGSLÄNK === */}
              <Paper elevation={0} sx={sectionCardSx}>
                <Typography sx={sectionTitleSx}>Delningslänk</Typography>
                <Box display="flex" alignItems="center" gap={0.75}>
                  <TextField
                    fullWidth
                    value={shareUrl}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.8rem', bgcolor: '#fff', borderRadius: '8px',
                      }
                    }}
                  />
                  <Tooltip title="Kopiera länk">
                    <IconButton size="small" onClick={handleCopyLink} sx={{ color: '#072f53' }}>
                      <ContentCopyIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Förhandsvisa">
                    <IconButton size="small" onClick={handlePreviewSharePage} sx={{ color: '#072f53' }}>
                      <OpenInNewIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Status chips */}
                <Box display="flex" flexWrap="wrap" gap={0.75} mt={1.5}>
                  {viewCount > 0 && (
                    <Chip
                      icon={<VisibilityIcon sx={{ fontSize: '14px !important' }} />}
                      label={`${viewCount} ${viewCount === 1 ? 'visning' : 'visningar'}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.72rem', height: 24 }}
                    />
                  )}
                  {passwordEnabled && (
                    <Chip
                      icon={<LockOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                      label="Lösenordsskyddad"
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.72rem', height: 24 }}
                    />
                  )}
                  {expiresAt && (
                    <Chip
                      icon={<ScheduleIcon sx={{ fontSize: '14px !important' }} />}
                      label={`Utgår ${new Date(expiresAt).toLocaleDateString('sv-SE')}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.72rem', height: 24 }}
                    />
                  )}
                </Box>

                {/* QR + Email row */}
                <Box display="flex" alignItems="center" gap={1.5} mt={2} pt={1.5} borderTop="1px solid rgba(0,0,0,0.06)">
                  <Box
                    onClick={() => setShowQrCode(!showQrCode)}
                    sx={{
                      cursor: 'pointer', border: '1px solid #e0e0e0', borderRadius: '6px',
                      p: 0.5, bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      '&:hover': { borderColor: '#072f53' }, transition: 'border-color 0.15s',
                    }}
                  >
                    {shareUrl ? (
                      <QRCodeSVG value={shareUrl} size={40} level="L" />
                    ) : (
                      <QrCodeIcon sx={{ fontSize: 40, color: '#ccc' }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <TextField
                        placeholder="E-postadress"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '0.82rem', bgcolor: '#fff', borderRadius: '8px',
                          }
                        }}
                      />
                      <Tooltip title="Skicka via e-post">
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleEmailShare}
                            disabled={!email || !shareUrl}
                            sx={{ color: '#072f53' }}
                          >
                            <EmailIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>

                {/* Expanded QR */}
                <Collapse in={showQrCode}>
                  <Box display="flex" flexDirection="column" alignItems="center" mt={2} pt={1.5} borderTop="1px solid rgba(0,0,0,0.06)">
                    <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#fff', border: '1px solid #e8e8e8', borderRadius: '8px', mb: 1.5 }}>
                      <QRCodeSVG id="collection-qrcode" value={shareUrl || 'placeholder'} size={160} level="H" includeMargin bgColor="#FFFFFF" fgColor="#000000" />
                    </Paper>
                    <Button variant="outlined" size="small" onClick={handleDownloadQRCode} startIcon={<DownloadIcon />} sx={{ textTransform: 'none', fontSize: '0.78rem' }}>
                      Ladda ner QR-kod
                    </Button>
                  </Box>
                </Collapse>
              </Paper>

              {/* === SECTION 2: ÅTKOMST & SÄKERHET === */}
              <Paper elevation={0} sx={sectionCardSx}>
                <Typography sx={sectionTitleSx}>Åtkomst & säkerhet</Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={<Switch checked={allowDownload} onChange={(e) => setAllowDownload(e.target.checked)} size="small" />}
                    label={<Typography sx={{ fontSize: '0.84rem' }}>Tillåt nedladdning</Typography>}
                  />
                  <FormControlLabel
                    control={<Switch checked={includeMetadata} onChange={(e) => setIncludeMetadata(e.target.checked)} size="small" />}
                    label={<Typography sx={{ fontSize: '0.84rem' }}>Ta med produktinformation</Typography>}
                  />
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={passwordToggle}
                          onChange={(e) => {
                            setPasswordToggle(e.target.checked);
                            if (!e.target.checked) {
                              setPassword('');
                              if (passwordEnabled) setRemovePassword(true);
                            } else {
                              setRemovePassword(false);
                            }
                          }}
                          size="small"
                        />
                      }
                      label={<Typography sx={{ fontSize: '0.84rem' }}>Lösenordsskydd</Typography>}
                    />
                    <Collapse in={passwordToggle} unmountOnExit>
                      <TextField
                        type="password"
                        autoComplete="new-password"
                        placeholder={passwordEnabled ? 'Ange nytt lösenord eller lämna tomt' : 'Ange lösenord (minst 4 tecken)'}
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setRemovePassword(false); }}
                        size="small"
                        fullWidth
                        sx={{
                          mt: 0.5, ml: 5.5, width: 'calc(100% - 44px)',
                          '& .MuiOutlinedInput-root': { fontSize: '0.82rem', bgcolor: '#fff', borderRadius: '8px' }
                        }}
                      />
                    </Collapse>
                  </Box>
                </Stack>
              </Paper>

              {/* === SECTION 3: GILTIGHETSTID === */}
              <Paper elevation={0} sx={sectionCardSx}>
                <Typography sx={sectionTitleSx}>Giltighetstid</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#888', mb: 1.5 }}>
                  Styr när samlingen och delningslänken är aktiva.
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#555', mb: 1 }}>Samling aktiv period</Typography>
                  <Stack direction="row" spacing={1.5}>
                    <TextField
                      label="Från"
                      type="datetime-local"
                      value={activeFrom}
                      onChange={(e) => setActiveFrom(e.target.value)}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem', bgcolor: '#fff', borderRadius: '8px' } }}
                    />
                    <TextField
                      label="Till"
                      type="datetime-local"
                      value={activeTo}
                      onChange={(e) => setActiveTo(e.target.value)}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem', bgcolor: '#fff', borderRadius: '8px' } }}
                    />
                  </Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={neverAutoDelete}
                        onChange={(e) => setNeverAutoDelete(e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography sx={{ fontSize: '0.84rem' }}>Radera aldrig samlingen automatiskt</Typography>}
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#555', mb: 1 }}>Delningslänk</Typography>
                  <Stack direction="row" spacing={1.5}>
                    <TextField
                      label="Utgår"
                      type="datetime-local"
                      value={expiresAt}
                      onChange={(e) => {
                        setExpiresAt(e.target.value);
                        setShareExpiryEdited(true);
                      }}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem', bgcolor: '#fff', borderRadius: '8px' } }}
                    />
                    <TextField
                      label="Max visningar"
                      type="number"
                      value={maxViews}
                      onChange={(e) => setMaxViews(e.target.value)}
                      size="small"
                      fullWidth
                      inputProps={{ min: 1 }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.82rem', bgcolor: '#fff', borderRadius: '8px' } }}
                    />
                  </Stack>
                  {showDefaultShareExpiryWarning && (
                    <Typography sx={{ color: '#d32f2f', fontSize: '0.76rem', mt: 0.75 }}>
                      {DEFAULT_SHARE_EXPIRY_WARNING}
                    </Typography>
                  )}
                </Box>
              </Paper>

              {/* === SECTION 4: UTSEENDE === */}
              <Paper elevation={0} sx={{ ...sectionCardSx, mb: 0 }}>
                <Typography sx={sectionTitleSx}>
                  Utseende
                  <Typography component="span" sx={{ fontSize: '0.68rem', color: '#aaa', ml: 1, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>valfritt</Typography>
                </Typography>
                <TextField
                  placeholder="Beskrivning (visas på den delade sidan)"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': { fontSize: '0.82rem', bgcolor: '#fff', borderRadius: '8px' }
                  }}
                />
                <Box>
                  <FormControlLabel
                    control={<Switch checked={bannerEnabled} onChange={(e) => setBannerEnabled(e.target.checked)} size="small" />}
                    label={<Typography sx={{ fontSize: '0.84rem' }}>Banner</Typography>}
                  />
                  <Collapse in={bannerEnabled}>
                    <Box mt={1} ml={5.5} sx={{ width: 'calc(100% - 44px)' }}>
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography sx={{ fontSize: '0.74rem', color: '#777', fontWeight: 600, mb: 0.75 }}>
                            Ladda upp
                          </Typography>
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<ImageIcon />}
                            disabled={uploadingBanner}
                            size="small"
                            sx={{ textTransform: 'none', fontSize: '0.78rem', borderRadius: '8px' }}
                          >
                            {uploadingBanner ? 'Laddar upp...' : bannerImagePath ? 'Byt banner' : 'Ladda upp banner'}
                            <input type="file" hidden accept="image/*" onChange={handleBannerUpload} />
                          </Button>
                        </Box>

                        <Box>
                          <Typography sx={{ fontSize: '0.74rem', color: '#777', fontWeight: 600, mb: 0.75 }}>
                            Sök i Athena
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <TextField
                              placeholder="Sök bannerbild i Athena"
                              value={bannerSearchQuery}
                              onChange={(e) => {
                                setBannerSearchQuery(e.target.value);
                                if (!e.target.value.trim()) {
                                  setBannerSearchPerformed(false);
                                  setBannerSearchResults([]);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleBannerSearch();
                                }
                              }}
                              size="small"
                              fullWidth
                              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.78rem', bgcolor: '#fff', borderRadius: '8px' } }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={searchingBannerAssets ? <CircularProgress size={14} /> : <SearchIcon />}
                              onClick={handleBannerSearch}
                              disabled={searchingBannerAssets}
                              sx={{ textTransform: 'none', borderRadius: '8px', whiteSpace: 'nowrap' }}
                            >
                              Sök
                            </Button>
                          </Stack>
                          {(bannerSearchPerformed || bannerSearchResults.length > 0) && (
                            <Box sx={{ mt: 1 }}>
                              {searchingBannerAssets
                                ? <Typography sx={{ fontSize: '0.75rem', color: '#999', py: 1 }}>Söker bilder...</Typography>
                                : renderBannerCandidateGrid(bannerSearchResults, 'Inga bilder hittades')}
                            </Box>
                          )}
                        </Box>

                        <Box>
                          <Typography sx={{ fontSize: '0.74rem', color: '#777', fontWeight: 600, mb: 0.75 }}>
                            Senast använda banners
                          </Typography>
                          {loadingRecentBanners
                            ? <Typography sx={{ fontSize: '0.75rem', color: '#999', py: 1 }}>Hämtar senaste bannerbilder...</Typography>
                            : renderBannerCandidateGrid(recentBanners, 'Inga tidigare bannerbilder hittades')}
                        </Box>
                      </Stack>
                      {bannerImagePath && (
                        <>
                          <Box
                            sx={{
                              mt: 1.5,
                              width: '100%', height: `${Math.min(bannerHeight, 200)}px`,
                              backgroundImage: `url(${getBannerImageUrl(bannerImagePath)})`,
                              backgroundSize: 'cover', backgroundPosition: `center ${bannerFocalY}%`,
                              borderRadius: '8px', border: '1px solid #e8e8e8', mb: 1.5,
                            }}
                          />
                          <Stack direction="row" spacing={2}>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>Höjd: {bannerHeight}px</Typography>
                              <Slider value={bannerHeight} onChange={(_e, val) => setBannerHeight(val as number)} min={100} max={600} step={10} size="small" />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>Fokus: {bannerFocalY}%</Typography>
                              <Slider value={bannerFocalY} onChange={(_e, val) => setBannerFocalY(val as number)} min={0} max={100} step={5} size="small" />
                            </Box>
                          </Stack>
                        </>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              </Paper>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', color: '#888' }}>
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSettings}
            disabled={saving || loading}
            sx={{
              textTransform: 'none', borderRadius: '8px', px: 3,
              bgcolor: '#072f53', '&:hover': { bgcolor: '#0a3f6e' },
            }}
          >
            {saving ? 'Sparar...' : 'Spara inställningar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={3000} 
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareCollectionDialog;
